import { completionCursor, useCompletionReadsStore } from "./neucleos/threadCompletionReads";
import {
  DEFAULT_THREAD_ERROR,
  getThreadErrorBannerKey,
  isThreadErrorBannerDismissedForSession,
  useThreadErrorDismissals,
} from "./threadErrorDismissals";
import { useAtomValue } from "@effect/atom-react";
import { useLocation } from "@tanstack/react-router";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import { scopeThreadRef, scopedThreadKey } from "@cleo/client-runtime/environment";
import {
  effectiveSettled,
  effectiveSnoozed,
  type ChangeRequestStateLike,
} from "@cleo/client-runtime/state/thread-settled";
import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";

import {
  composerDraftHasUserContent,
  DraftId,
  useComposerContentStore,
  useComposerDraftSessionStore,
  type DraftThreadState,
} from "./composerDraftStore";
import { useClientSettings } from "./hooks/useSettings";
import { useNowMinute } from "./hooks/useNowMinute";
import { useNeucleosWorldStore } from "./neucleos/worldStore";
import { allModeActive, localModeActive } from "./neucleos/worldSelection";
import {
  projectRootSessionStrip,
  type RootSessionAddress,
  type RootSessionStripDraftInput,
  type RootSessionStripThreadInput,
  type RootSessionStatus,
  type RootSessionTab,
} from "./rootSessionStrip";
import {
  hasUnseenCompletion,
  hasUnseenFailure,
  resolveSidebarThreadStatus,
} from "./components/Sidebar.logic";
import { environmentServerConfigsAtom } from "./state/server";
import { useThreadShells } from "./state/entities";
import { useThreadChangeRequestStateStore } from "./threadChangeRequestStateStore";
import { parseSessionSessionLeafPath } from "./worldViewRoute";

// RM-CAN-14 (2026-09-20): the Local strip's view, its launch form and its
// "+New Child" row are deleted; Local's rows and verbs stand on the Room Rail
// (components/RoomRailLocalOrgans.tsx). What remains is read by other seats:
// the per-thread status inputs, and the projection the Local frame derives
// its room boundary from.

// The adapter half of the root-session strip (Arc A1). Everything here is
// read-and-project: the only side effect the strip can cause is a route
// navigation. Lifecycle classification reuses the exact client-runtime rules
// the sidebar partition uses, in the sidebar's precedence order
// (snooze > pin > settle), so the strip and the sidebar cannot disagree about
// what counts as live work. Change-request state is an ephemeral VCS
// projection shared with both sidebar implementations; it never becomes a
// second durable lifecycle record.

export interface ShellLifecycleOptions {
  readonly now: string;
  readonly preciseNow: string;
  readonly supportsSnooze: boolean;
  readonly supportsSettlement: boolean;
  readonly autoSettleAfterDays: number | null;
  readonly changeRequestState: ChangeRequestStateLike | null;
}

export function shellToStripThread(
  shell: EnvironmentThreadShell,
  options: ShellLifecycleOptions,
): RootSessionStripThreadInput {
  const snoozed = options.supportsSnooze && effectiveSnoozed(shell, { now: options.preciseNow });
  const pinned = !snoozed && shell.pinnedAt != null;
  const settled =
    !snoozed &&
    !pinned &&
    options.supportsSettlement &&
    effectiveSettled(shell, {
      now: options.now,
      autoSettleAfterDays: options.autoSettleAfterDays,
      changeRequestState: options.changeRequestState,
    });
  return {
    environmentId: shell.environmentId,
    threadId: shell.id,
    worldKey: shell.worldKey ?? null,
    sessionId: shell.sessionId ?? null,
    archivedAt: shell.archivedAt ?? null,
    pinnedAt: pinned ? (shell.pinnedAt ?? null) : null,
    snoozedUntil: snoozed ? (shell.snoozedUntil ?? options.preciseNow) : null,
    settledAt: settled ? (shell.settledAt ?? options.now) : null,
    parentThreadId: shell.parentThreadId ?? null,
    title: shell.title ?? null,
  };
}

export function shellRootSessionStatus(
  shell: EnvironmentThreadShell,
  lastVisitedAt: string | null | undefined,
  errorDismissed = false,
): RootSessionStatus | null {
  const status = resolveSidebarThreadStatus(shell);
  // A failure stays lit until it has been seen: dismissed in this browser, or
  // visited since it happened. Never forever (18 September 2026).
  if (
    status === "failed" &&
    (errorDismissed || !hasUnseenFailure({ ...shell, lastVisitedAt: lastVisitedAt ?? undefined }))
  )
    return shell.backgroundLiveness ?? null;
  if (status !== "ready") return status;
  return hasUnseenCompletion({ ...shell, lastVisitedAt: lastVisitedAt ?? undefined })
    ? "done"
    : null;
}

/**
 * When a session's work ended, for the agent strip's Finished cards. A failed
 * session takes the session's own error stamp (the same one hasUnseenFailure
 * reads), never an earlier turn's completion; a finished one takes its latest
 * turn's completion (the one hasUnseenCompletion reads).
 */
export function shellFinishedAt(shell: {
  readonly session?: { readonly status: string; readonly updatedAt?: string | null } | null;
  readonly latestTurn?: { readonly completedAt: string | null } | null;
}): string | null {
  if (shell.session?.status === "error") return shell.session.updatedAt ?? null;
  return shell.latestTurn?.completedAt ?? null;
}

export function nextRootSessionWakeDelayMs(
  threads: ReadonlyArray<RootSessionStripThreadInput>,
  nowMs: number,
): number | null {
  let nextWakeAtMs = Number.POSITIVE_INFINITY;
  for (const thread of threads) {
    if (thread.snoozedUntil === null) continue;
    const wakeAtMs = Date.parse(thread.snoozedUntil);
    if (!Number.isNaN(wakeAtMs) && wakeAtMs < nextWakeAtMs) nextWakeAtMs = wakeAtMs;
  }
  if (!Number.isFinite(nextWakeAtMs)) return null;
  return Math.min(Math.max(0, nextWakeAtMs - nowMs) + 50, 2_147_483_647);
}

export function draftSessionsToStripDrafts(input: {
  readonly draftThreadsByThreadKey: Readonly<Record<string, DraftThreadState>>;
  readonly hasComposerContent: (draftKey: string) => boolean;
  readonly addressedDraftId: string | null;
}): RootSessionStripDraftInput[] {
  const drafts: RootSessionStripDraftInput[] = [];
  for (const [draftKey, session] of Object.entries(input.draftThreadsByThreadKey)) {
    if (session.promotedTo != null) continue;
    // The sidebar's invested-draft rule, plus the addressed exception: the
    // draft being looked at is always a tab, an abandoned empty draft is not.
    if (draftKey !== input.addressedDraftId && !input.hasComposerContent(draftKey)) continue;
    drafts.push({
      draftId: draftKey,
      environmentId: session.environmentId,
      threadId: session.threadId,
      worldKey: session.worldKey,
      promotedTo: session.promotedTo ?? null,
      title: null,
    });
  }
  drafts.sort((left, right) => left.draftId.localeCompare(right.draftId));
  return drafts;
}

export function rootSessionTabNavigation(tab: RootSessionTab):
  | { readonly to: "/draft/$draftId"; readonly params: { readonly draftId: string } }
  | {
      readonly to: "/$environmentId/$threadId";
      readonly params: { readonly environmentId: string; readonly threadId: string };
    } {
  if (tab.kind === "draft" && tab.draftId !== null) {
    return { to: "/draft/$draftId", params: { draftId: tab.draftId } };
  }
  return {
    to: "/$environmentId/$threadId",
    params: { environmentId: tab.environmentId, threadId: tab.threadId },
  };
}

export function selectRootSessionTab(
  tab: RootSessionTab,
  navigate: (target: ReturnType<typeof rootSessionTabNavigation>) => void,
): void {
  if (tab.selected) return;
  navigate(rootSessionTabNavigation(tab));
}

/**
 * The addressed leaf, parsed from the pathname. Route params would be the
 * richer source, but this container renders in the shared _chat layout above
 * the leaf match; the pathname is the one address both history modes share.
 */
export const parseSessionLeafPath = parseSessionSessionLeafPath;

/**
 * The strip-shaped input for EVERY thread the client knows — all Worlds, all
 * environments — with lifecycle resolved through the same client-runtime rules
 * the sidebar uses and status through the one sidebar resolver. World doors and
 * Session tabs fold over this same array, so no chrome surface can disagree with
 * the session strip about what a session is doing.
 */
export function useRootSessionThreadInputs(): ReadonlyArray<RootSessionStripThreadInput> {
  const shells = useThreadShells();
  const serverConfigs = useAtomValue(environmentServerConfigsAtom);
  const autoSettleAfterDays = useClientSettings((s) => s.sidebarAutoSettleAfterDays);
  const nowMinute = useNowMinute();
  const [snoozeWakeTick, bumpSnoozeWakeTick] = useState(0);
  const changeRequestStateByKey = useThreadChangeRequestStateStore((state) => state.byThreadKey);
  const completionBuckets = useCompletionReadsStore((state) => state.buckets);
  const completionIdentity = useNeucleosWorldStore((state) =>
    state.signedIn ? state.identity : null,
  );
  const completionWorlds = useNeucleosWorldStore((state) => state.worlds);
  const errorDismissals = useThreadErrorDismissals((state) => state.byThread);

  const threads = useMemo(() => {
    void snoozeWakeTick;
    const preciseNow = new Date().toISOString();
    const now = `${nowMinute}:00.000Z`;
    return shells.map((shell) => {
      const threadKey = scopedThreadKey(scopeThreadRef(shell.environmentId, shell.id));
      return {
        ...shellToStripThread(shell, {
          now,
          preciseNow,
          supportsSnooze:
            serverConfigs.get(shell.environmentId)?.environment.capabilities.threadSnooze === true,
          supportsSettlement:
            serverConfigs.get(shell.environmentId)?.environment.capabilities.threadSettlement ===
            true,
          autoSettleAfterDays,
          changeRequestState: changeRequestStateByKey.get(threadKey) ?? null,
        }),
        finishedAt: shellFinishedAt(shell),
        status: shellRootSessionStatus(
          shell,
          completionCursor(
            completionBuckets,
            shell.worldKey
              ? completionIdentity
                ? {
                    identity: completionIdentity,
                    worldKey: shell.worldKey,
                    ...(completionWorlds?.find((world) => world.worldKey === shell.worldKey)
                      ?.actorId
                      ? {
                          actorId: completionWorlds.find(
                            (world) => world.worldKey === shell.worldKey,
                          )!.actorId!,
                        }
                      : {}),
                  }
                : undefined
              : null,
            threadKey,
          ),
          isThreadErrorBannerDismissedForSession(
            getThreadErrorBannerKey(
              threadKey,
              shell.session?.lastError ??
                (shell.session?.status === "error" ? DEFAULT_THREAD_ERROR : null),
              shell.session?.updatedAt ?? null,
              JSON.stringify([
                completionIdentity,
                shell.worldKey ?? null,
                completionWorlds.find((world) => world.worldKey === shell.worldKey)?.actorId ??
                  null,
              ]),
            ),
            errorDismissals,
          ),
        ),
      };
    });
  }, [
    autoSettleAfterDays,
    changeRequestStateByKey,
    nowMinute,
    serverConfigs,
    shells,
    snoozeWakeTick,
    completionBuckets,
    completionIdentity,
    completionWorlds,
    errorDismissals,
  ]);

  useEffect(() => {
    const delayMs = nextRootSessionWakeDelayMs(threads, Date.now());
    if (delayMs === null) return;
    const id = window.setTimeout(() => bumpSnoozeWakeTick((tick) => tick + 1), delayMs);
    return () => window.clearTimeout(id);
  }, [threads]);

  return threads;
}

function useRootSessionStripProjectionValue() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const leaf = parseSessionLeafPath(pathname);

  const threads = useRootSessionThreadInputs();
  const shells = useThreadShells();
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const localDoor = useNeucleosWorldStore((state) =>
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    }),
  );
  const allDoor = useNeucleosWorldStore((state) => allModeActive(state.selection));
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const draftThreadsByThreadKey = useComposerDraftSessionStore(
    (store) => store.draftThreadsByThreadKey,
  );
  const draftsByThreadKey = useComposerContentStore((store) => store.draftsByThreadKey);

  const addressedDraftId = leaf?.kind === "draft" ? leaf.draftId : null;
  const addressedDraftSession = useComposerDraftSessionStore((store) =>
    addressedDraftId === null ? null : store.getDraftSession(DraftId.make(addressedDraftId)),
  );

  const addressed: RootSessionAddress | null = useMemo(() => {
    if (leaf?.kind === "thread") {
      return { environmentId: leaf.environmentId, threadId: leaf.threadId };
    }
    if (addressedDraftSession !== null && addressedDraftSession !== undefined) {
      return {
        environmentId: addressedDraftSession.environmentId,
        threadId: addressedDraftSession.threadId,
      };
    }
    return null;
  }, [leaf, addressedDraftSession]);

  // Arc A6: the whole recursive projection — typed state, derived address
  // chain, and one sibling strip per level — computed here, persisted nowhere.
  const projection = useMemo(() => {
    const drafts = draftSessionsToStripDrafts({
      draftThreadsByThreadKey,
      hasComposerContent: (draftKey) => {
        const composer = draftsByThreadKey[draftKey];
        return composer !== undefined && composerDraftHasUserContent(composer);
      },
      addressedDraftId,
    });
    return projectRootSessionStrip({
      activeWorldKey,
      availableWorldKeys: worlds.map((world) => world.worldKey),
      local: localDoor,
      all: allDoor,
      threads,
      drafts,
      addressed,
    });
  }, [
    activeWorldKey,
    addressed,
    allDoor,
    addressedDraftId,
    draftThreadsByThreadKey,
    draftsByThreadKey,
    localDoor,
    threads,
    worlds,
  ]);
  return { projection } as const;
}

type RootSessionStripProjectionValue = ReturnType<typeof useRootSessionStripProjectionValue>;

const RootSessionStripProjectionContext = createContext<RootSessionStripProjectionValue | null>(
  null,
);

/**
 * One projection owner for the root row outside the Session and every nested row
 * inside it. A standalone strip supplies this provider automatically so the
 * existing focused component tests keep exercising the real adapter.
 */
export function RootSessionStripProjectionProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const value = useRootSessionStripProjectionValue();
  return (
    <RootSessionStripProjectionContext.Provider value={value}>
      {children}
    </RootSessionStripProjectionContext.Provider>
  );
}

export function useRootSessionStripProjection(): RootSessionStripProjectionValue {
  const value = useContext(RootSessionStripProjectionContext);
  if (value === null) {
    throw new Error("Root session projection is outside its Session frame");
  }
  return value;
}
